import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// ⚖️ LEGAL REVIEW REMINDER ⚖️
// This Terms of Service is a first-pass draft, NOT lawyer-reviewed.
// REQUIRED ACTION: Have a Canadian lawyer review BEFORE ~500 active users
// OR before charging paying customers, whichever comes first.
// See also: /app/memory/LEGAL_REVIEW_REMINDER.md

const LAST_UPDATED = 'May 14, 2026';
const COMPANY = 'Live in Vibrancy Coaching Ltd.';
const APP_NAME = 'Inner Work';
const CONTACT_EMAIL = 'client_care@liveinvibrancy.com';
const GOVERNING_LAW = 'the Province of Alberta, Canada';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-sand relative overflow-hidden">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
        <Link
          to="/login"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase mb-8"
          style={{ transition: 'color 0.2s ease' }}
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">{APP_NAME}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading mb-3">Terms of Service</h1>
        <p className="text-text-muted font-sans text-xs tracking-wider mb-2">Last updated: {LAST_UPDATED}</p>
        <div className="mystical-divider my-8 max-w-[80px]" />

        <div className="prose-content space-y-8 font-sans text-base text-dark leading-relaxed">

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3 mt-2">1. Agreement</h2>
            <p>
              These Terms of Service ("Terms") form a binding agreement between you and {COMPANY} ("we", "us", "our") and govern your access to and use of {APP_NAME} (the "Service"). By creating an account, signing in, or otherwise using the Service, you agree to these Terms and our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">2. Eligibility</h2>
            <p>
              You must be at least 16 years old (or the age of majority in your jurisdiction, whichever is higher) to use {APP_NAME}. By using the Service you represent that you meet this requirement and that you can lawfully enter into this agreement.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">3. Not Medical or Mental-Health Care</h2>
            <p className="mb-3">
              {APP_NAME} is a self-development tool. The AI coach, breathwork, journal prompts, meditations, and other features are <strong>not a substitute for professional medical, psychological, psychiatric, or therapeutic care</strong>. The Service is not designed to diagnose, treat, cure, or prevent any medical or mental-health condition.
            </p>
            <p className="mb-3">
              If you are experiencing a mental-health emergency, suicidal ideation, or crisis, contact your local emergency services or a qualified crisis line immediately. In Canada and the U.S., you can call or text <strong>988</strong> (Suicide Crisis Helpline). Outside North America, please contact your country's equivalent crisis service.
            </p>
            <p>
              Use of the Service is at your own discretion. You are responsible for evaluating whether the practices offered are appropriate for you, and you should consult a licensed professional before making changes to any treatment plan.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">4. Your Account</h2>
            <p>
              You are responsible for keeping your login credentials secure and for any activity that occurs under your account. Please notify us at <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> if you suspect unauthorized access.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">5. Acceptable Use</h2>
            <p className="mb-3">You agree NOT to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violate any law or the rights of others.</li>
              <li>Submit content that is unlawful, harmful, defamatory, or that infringes others' rights.</li>
              <li>Attempt to gain unauthorized access to the Service or its systems, or to interfere with its operation.</li>
              <li>Reverse-engineer, scrape, or copy non-public portions of the Service.</li>
              <li>Use automated means (bots, scripts) to interact with the Service without our written consent.</li>
              <li>Resell, repackage, or commercially redistribute the Service.</li>
              <li>Submit input designed to manipulate the AI coach into producing harmful or unlawful output.</li>
            </ul>
            <p className="mt-3">We may suspend or terminate accounts that violate these rules.</p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">6. Your Content</h2>
            <p>
              You retain ownership of the content you create on {APP_NAME} (journal entries, glimmers, milestones, identity declarations, etc.). By submitting content, you grant {COMPANY} a limited, worldwide, royalty-free license to host, store, process, and display your content solely for the purpose of operating and improving the Service for you. We do not sell your content. We do not use identifiable content to train external AI models.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">7. Intellectual Property</h2>
            <p>
              The Service, its software, design, branding, prompts, frameworks (including the Integrated Core Story / Sovereign Alchemist Protocol), guided meditations, and other content provided by us are owned by {COMPANY} or its licensors and are protected by intellectual-property laws. Except for the limited right to use the Service as permitted by these Terms, no rights are granted to you.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">8. AI Output</h2>
            <p>
              AI-generated responses may be inaccurate, incomplete, or inappropriate. You should use your own judgment before acting on AI suggestions. {COMPANY} is not responsible for decisions you make based on AI output.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">9. Fees, Plans & Cancellation</h2>
            <p>
              Some features of the Service may be offered for a fee, with terms presented at the point of purchase. Subscription terms (billing period, renewal, cancellation) will be disclosed at sign-up. You may cancel at any time from your account settings; cancellations take effect at the end of the current billing period. Unless required by law, fees already paid are non-refundable.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">10. Termination</h2>
            <p>
              You may stop using the Service or delete your account at any time. We may suspend or terminate your account if you violate these Terms, if continued provision becomes commercially unreasonable, or if required by law. Sections that by their nature should survive termination (intellectual property, disclaimers, limitation of liability, dispute resolution) will continue to apply.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">11. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI OUTPUT WILL BE ACCURATE OR RELIABLE.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">12. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} AND ITS OFFICERS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, REVENUES, DATA, OR GOODWILL, ARISING FROM OR RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING UNDER THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) CAD $100.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">13. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless {COMPANY} from any claims, damages, or expenses (including reasonable attorneys' fees) arising from your violation of these Terms, your content, or your misuse of the Service.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">14. Governing Law & Disputes</h2>
            <p>
              These Terms are governed by the laws of {GOVERNING_LAW}, without regard to conflict-of-law rules. Any dispute will be brought in the courts located in that jurisdiction, except where mandatory consumer-protection law gives you a right to a different forum.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">15. Changes</h2>
            <p>
              We may update these Terms from time to time. Material changes will be announced through the Service or by email at least 14 days in advance. Continued use after the effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-light text-dark mb-3">16. Contact</h2>
            <p>
              Questions: <a className="text-gold underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> · {COMPANY}, 13920 117 Ave NW, Edmonton, Alberta T5M 3J4, Canada.
            </p>
          </section>
        </div>

        <div className="mystical-divider my-12 mx-auto max-w-[80px]" />
        <p className="text-center text-text-muted font-sans text-xs tracking-wider">
          <Link to="/privacy" className="hover:text-dark">Privacy Policy</Link>
          <span className="mx-3">·</span>
          <Link to="/login" className="hover:text-dark">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
